/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { If, Then } from "react-if";
import {
  BrickLoader,
  FlexContainer,
  Modal,
  Text,
  theme,
  Button,
} from "@nordcloud/gnui";
import { NoData, Pagination } from "~/components";
import { LoaderWrap } from "~/components/Loader/LoaderWrap";
import { ModalContent, ModalFooter } from "~/components/Styled";
import type { ComplianceStatus } from "../types";
import { UpdatesOverviewHeader, UpdatesTable } from "./components";
import { missingActionLogsSections } from "./const";
import { useComplianceReport } from "./hooks/useComplianceReport";

const PAGINATION_AREA_MIN_HEIGHT = "60px";
const MODAL_CONTENT_MAX_HEIGHT = "70vh";
const TABLE_LOADER_HEIGHT = "40vh";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  resourceId: string;
  resource?: {
    id: string;
    name?: string | null;
    os?: string | null;
  };
  complianceStatus: ComplianceStatus;
};

export function UpdatesModal({
  isOpen,
  onClose,
  resourceId,
  resource,
  complianceStatus,
}: Props) {
  const {
    reportPeriod,
    isLoading,
    isFailed,
    timedOut,
    showGeneratingMessage,
    retry,
    detailsItems,
    updateStatusChartItems,
    derivedOverview,
    filteredAndSortedUpdates,
    tableState,
    selectedStatuses,
    setSelectedStatuses,
    updateSearchState,
    searchState,
    hasUpdates,
    actionsWithMissingDataByErrCode,
  } = useComplianceReport({ resourceId, complianceStatus, resource });

  const missingLogSections = useMemo(
    () =>
      missingActionLogsSections
        .map(({ key, title }) => ({
          key,
          title,
          actionsWithMissingData: actionsWithMissingDataByErrCode[key] ?? [],
        }))
        .filter((section) => section.actionsWithMissingData.length > 0),
    [actionsWithMissingDataByErrCode]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      contentLabel="Compliance Report"
      contentMaxHeight="90vh"
      modalMinWidth="90vw"
    >
      <ModalContent
        css={{
          maxHeight: MODAL_CONTENT_MAX_HEIGHT,
          minHeight: MODAL_CONTENT_MAX_HEIGHT,
        }}
      >
        <FlexContainer direction={"column"} gap={theme.spacing.spacing06}>
          <UpdatesOverviewHeader
            detailsItems={detailsItems}
            updateStatusChartItems={updateStatusChartItems}
            loadingStatus={isLoading}
            hasUpdates={hasUpdates}
          />

          <If
            condition={
              isLoading && !isFailed && !timedOut && !showGeneratingMessage
            }
          >
            <Then>
              <LoaderWrap
                loading
                Component={() => <BrickLoader height={TABLE_LOADER_HEIGHT} />}
              >
                <></>
              </LoaderWrap>
            </Then>
          </If>
          <If
            condition={
              isLoading && !isFailed && !timedOut && showGeneratingMessage
            }
          >
            <Then>
              <FlexContainer
                direction="column"
                alignItems="center"
                gap={theme.spacing.spacing02}
                css={{ padding: theme.spacing.spacing06 }}
              >
                <Text size="lg" weight="medium">
                  Generating compliance report...
                </Text>
                <Text color={theme.color.text.text02}>
                  This can take up to ~30 seconds. If it takes longer, please
                  retry.
                </Text>
              </FlexContainer>
            </Then>
          </If>
          <If condition={isFailed || timedOut}>
            <Then>
              <FlexContainer
                direction="column"
                alignItems="center"
                gap={theme.spacing.spacing05}
                css={{ padding: theme.spacing.spacing04 }}
              >
                <NoData
                  hasIcon
                  message={
                    isFailed
                      ? "Report generation failed. Please retry."
                      : "Report is still generating. Please retry."
                  }
                  customCss={{ padding: 0 }}
                />
                <Button size="md" severity="high" onClick={retry}>
                  Retry
                </Button>
              </FlexContainer>
            </Then>
          </If>
          <If condition={!isFailed && !timedOut && !isLoading}>
            <Then>
              <UpdatesTable
                data={derivedOverview}
                loading={isLoading}
                tableState={tableState}
                updates={filteredAndSortedUpdates.updates}
                missingLogSections={missingLogSections}
                resource={resource}
                resourceId={resourceId}
                reportPeriod={reportPeriod}
                hasUpdates={hasUpdates}
                selectedStatuses={selectedStatuses}
                onChangeStatus={(status) => {
                  setSelectedStatuses(status);
                  updateSearchState({ page: 1 });
                }}
                onSearch={(value) => {
                  const baseFilter = searchState.filter ?? {};
                  const nextFilter = value
                    ? { ...baseFilter, phrase: value }
                    : (({ phrase: _omit, ...rest }) => rest)(baseFilter);

                  updateSearchState({ page: 1, filter: nextFilter });
                }}
                onSortField={(field) =>
                  updateSearchState({
                    sort: {
                      field,
                      order: tableState.sortDirection,
                    },
                    page: 1,
                  })
                }
                onSortDirection={(dir) =>
                  updateSearchState({
                    sort: {
                      field: tableState.sortField,
                      order: dir,
                    },
                  })
                }
              />
            </Then>
          </If>
        </FlexContainer>
      </ModalContent>

      <ModalFooter
        mt={theme.spacing.spacing02}
        justifyContent="flex-end"
        css={{ paddingTop: 0 }}
      >
        <div css={{ width: "100%", minHeight: PAGINATION_AREA_MIN_HEIGHT }}>
          <If condition={!isFailed && !timedOut && !isLoading}>
            <Then>
              <Pagination
                count={filteredAndSortedUpdates.total}
                page={tableState.page}
                limit={tableState.limit}
                onPageChange={(page) => updateSearchState({ page })}
                onLimitChange={(limit) => updateSearchState({ limit, page: 1 })}
              />
            </Then>
          </If>
        </div>
      </ModalFooter>
    </Modal>
  );
}
